import { Link } from 'react-router-dom';
import {
  contestSponsors,
  sponsorshipAssignments,
  contestSponsorDashboard,
  tierConfigs,
  entityTypeConfig,
  getActiveAssignments,
  formatMoney,
  formatCompactNum,
  getTierConfig,
  getEntityTypeConfig,
  type SponsorTier,
} from '@/mocks/rfwContestSponsors';

const csd = contestSponsorDashboard;
const activeAssignments = getActiveAssignments();
const topSponsors = [...contestSponsors].sort((a, b) => b.totalInvestment - a.totalInvestment).slice(0, 6);
const byEntityCount: Record<string, number> = {};
activeAssignments.forEach((a) => {
  byEntityCount[a.entityType] = (byEntityCount[a.entityType] || 0) + 1;
});

function TierBadge({ tier }: { tier: SponsorTier }) {
  const cfg = getTierConfig(tier);
  if (!cfg) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
      style={{ backgroundColor: cfg.color + '18', color: cfg.color, border: '1px solid ' + cfg.color + '30' }}
    >
      <i className={`${cfg.icon} text-[9px]`}></i>
      {cfg.label}
    </span>
  );
}

export default function PatrocinadoresSection() {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-background-100">
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/4 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/3 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-5">
            <i className="ri-hand-heart-line text-xs"></i>
            Patrocinadores de Concursos y Eventos
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Tu concurso,{' '}
            <span className="text-amber-600">tus patrocinadores</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Las radios venden sus propios patrocinadores para concursos y eventos. RFW no interviene — la ganancia es 100% de la radio.
          </p>

          {/* 100% Radio highlight */}
          <div className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-emerald-50 border border-emerald-200">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
              <i className="ri-check-double-line text-white text-sm"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-extrabold text-emerald-700">100% Radio</p>
              <p className="text-[10px] text-emerald-600">RFW No Interviene en la Venta</p>
            </div>
          </div>
        </div>

        {/* Dashboard KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-amber-600 tabular-nums leading-tight">{csd.activeSponsors}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Patrocinadores</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-emerald-600 tabular-nums leading-tight">{csd.activeCampaigns}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Patrocinios Activos</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatMoney(csd.totalRevenue)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Ingresos Totales</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden sm:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{csd.avgCtr}%</div>
            <div className="text-[10px] text-foreground-400 font-medium">CTR Promedio</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden lg:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatCompactNum(csd.totalImpressions)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Impresiones</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden lg:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{csd.avgConversion}%</div>
            <div className="text-[10px] text-foreground-400 font-medium">Conversión</div>
          </div>
        </div>

        {/* Main layout: 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {/* Col 1: Top Sponsors */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
                <i className="ri-star-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Patrocinadores Top</h3>
            </div>
            <div className="space-y-2">
              {topSponsors.map((s, idx) => {
                const cfg = getTierConfig(s.tier);
                return (
                  <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-100/60 transition-colors cursor-pointer">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold tabular-nums flex-shrink-0">{idx + 1}</span>
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={s.logo} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-foreground-950 truncate">{s.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-foreground-500">{s.sector}</span>
                        <span className="text-[10px] text-foreground-400">·</span>
                        <span className="text-[10px] text-amber-600 font-semibold">{formatMoney(s.totalInvestment)}</span>
                      </div>
                    </div>
                    {cfg && (
                      <span className="text-[9px] font-semibold whitespace-nowrap px-1.5 py-0.5 rounded-full" style={{ backgroundColor: cfg.color + '15', color: cfg.color, border: '1px solid ' + cfg.color + '25' }}>
                        {cfg.label.split(' ')[1] || cfg.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Col 2: Patrocinios por tipo de entidad */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 text-rose-600">
                <i className="ri-links-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">¿Qué se Puede Patrocinar?</h3>
            </div>
            <div className="space-y-2.5">
              {entityTypeConfig.map((et) => {
                const count = byEntityCount[et.id] || 0;
                return (
                  <div key={et.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-background-100/60 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg ${et.color} flex items-center justify-center`}>
                        <i className={`${et.icon} text-sm ${et.textColor}`}></i>
                      </div>
                      <span className="text-xs font-medium text-foreground-800">{et.label}</span>
                    </div>
                    <span className="text-xs font-bold text-foreground-500 tabular-nums">{count} activo{count !== 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Col 3: Tiers explicados */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-500/10 text-purple-600">
                <i className="ri-stack-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Niveles de Patrocinio</h3>
            </div>
            <div className="space-y-2">
              {tierConfigs.map((t) => (
                <div key={t.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-background-100/60 transition-colors cursor-pointer">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.color + '20' }}>
                    <i className={`${t.icon} text-xs`} style={{ color: t.color }}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-950 leading-tight">{t.label}</p>
                    <p className="text-[10px] text-foreground-500 leading-tight">{t.description}</p>
                  </div>
                  <span className="text-[10px] font-semibold text-foreground-600 whitespace-nowrap">{t.priceRange}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Active sponsorships strip */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
              <i className="ri-flashlight-line text-sm"></i>
            </div>
            <h3 className="text-sm font-bold text-foreground-950">Patrocinios Activos Ahora</h3>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {activeAssignments.slice(0, 8).map((a) => {
              const sponsor = contestSponsors.find((s) => s.id === a.sponsorId);
              const entCfg = getEntityTypeConfig(a.entityType);
              const tierCfg = getTierConfig(a.tier);
              return (
                <div key={a.id} className="rounded-xl border border-background-200/70 bg-background-50 p-4 hover:border-amber-300/50 transition-all duration-200 cursor-pointer group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={a.sponsorLogo} alt={a.sponsorName} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-foreground-950 truncate">{a.sponsorName}</p>
                    </div>
                    {tierCfg && <TierBadge tier={a.tier} />}
                  </div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {entCfg && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${entCfg.color} ${entCfg.textColor}`}>
                        <i className={`${entCfg.icon} text-[8px] mr-0.5`}></i>
                        {entCfg.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-700 font-medium truncate">{a.entityName}</p>
                  <p className="text-[10px] text-foreground-500 mt-1">{a.radioName} {a.radioFrequency}</p>
                  <div className="mt-2 pt-2 border-t border-background-200/70 flex items-center justify-between">
                    <span className="text-xs font-extrabold text-amber-600 tabular-nums">{formatMoney(a.investment)}</span>
                    <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium">Activo</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA + Rule */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-5 rounded-2xl bg-gradient-to-r from-emerald-50/80 to-amber-50/80 border border-emerald-200/50">
          <div className="flex items-center gap-4 md:gap-8 flex-wrap justify-center sm:justify-start">
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-check-double-line text-emerald-500 text-base"></i>
              <span>Vos vendés · <strong className="text-foreground-950">Vos cobrás</strong></span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-emerald-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-shield-check-line text-emerald-500 text-base"></i>
              <span>RFW <strong className="text-foreground-950">no interviene</strong> en la venta</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-emerald-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-bank-line text-emerald-500 text-base"></i>
              <span>Ganancia <strong className="text-foreground-950">{csd.radioRevenuePercent}% Radio</strong></span>
            </div>
          </div>
          <Link
            to="/patrocinadores"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
          >
            Ver Patrocinadores
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}